import React, { useState } from "react";
import { useNavigate, useLocation, Location } from "react-router-dom";
import { useDescope, useSession } from "@descope/react-sdk";
import { useAlert } from "context/AlertContext";
import { LoginLayout, LoginForm } from "components/organisms";
import { LoginFormData, AuthError } from "types/auth.types";
import useAuthStore from "stores/authStore";
import { analyticsService } from "services/analyticsService";

interface LocationState {
  from?: Location;
}

interface UiMessage {
  kind: 'auth' | 'rate-limit' | 'policy' | 'validation' | 'network' | 'server' | 'unknown';
  text: string;
}

async function extractDescopeError(errOrResult: any): Promise<AuthError> {
  if (errOrResult instanceof Response) {
    let payload: any = {};
    try {
      payload = await errOrResult.clone().json();
    } catch (_) {}
    return {
      status: errOrResult.status,
      code: payload?.errorCode || payload?.code,
      desc: payload?.errorDescription || payload?.message || "Request failed",
    };
  }

  if (errOrResult && typeof errOrResult === "object") {
    const status = errOrResult.status || errOrResult.code;
    const code =
      (errOrResult.error && errOrResult.error.errorCode) ||
      errOrResult.errorCode ||
      errOrResult.code;
    const desc =
      (errOrResult.error && errOrResult.error.errorDescription) ||
      errOrResult.errorDescription ||
      errOrResult.message;
    if (status || code || desc) return { status, code, desc };
  }

  if (errOrResult instanceof TypeError) {
    return { status: undefined, code: "err_network", desc: errOrResult.message || "Network error" };
  }

  if (errOrResult && typeof errOrResult.message === "string") {
    return { status: undefined, code: "err_unknown", desc: errOrResult.message };
  }

  return { status: undefined, code: "err_unknown", desc: "Unknown error" };
}

function mapToUiMessage({ status, code, desc }: AuthError): UiMessage {
  const isWrongCreds =
    status === 401 ||
    code === "err_invalid_credentials" ||
    code === "err_user_not_found" ||
    /^E0629/.test(code || "") ||
    /signin failed/i.test(desc || "");

  if (isWrongCreds) {
    return { kind: "auth", text: "Invalid email or password." };
  }

  if (status === 429 || code === "err_too_many_attempts") {
    return { kind: "rate-limit", text: "Too many attempts. Please wait a minute and try again." };
  }

  if (
    status === 403 ||
    code === "err_domain_not_allowed" ||
    code === "err_login_disabled" ||
    code === "err_user_blocked"
  ) {
    return { kind: "policy", text: "Sign-in is restricted for this account." };
  }

  if (code === "err_validation" || code === "err_invalid_arguments") {
    return { kind: "validation", text: "Please check your inputs and try again." };
  }

  if (code === "err_network") {
    return { kind: "network", text: "Cannot reach the server. Check your connection and retry." };
  }

  if (status && status >= 500) {
    return { kind: "server", text: "Authentication service is temporarily unavailable. Please try again shortly." };
  }

  return { kind: "unknown", text: desc || "Something went wrong. Please try again." };
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const sdk = useDescope();
  const { isSessionLoading } = useSession();
  const { showSuccess, showError, showWarning } = useAlert();
  const { setAuth } = useAuthStore();

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const from = (location.state as LocationState)?.from?.pathname || "/";

  const handleLogin = async (formData: LoginFormData): Promise<void> => {
    setLoading(true);
    setError("");

    try {
      const result = await sdk.password.signIn(formData.email.trim(), formData.password);

      console.log('[Login] SignIn result:', result);

      if (result && (result as any).ok === false) {
        const norm = await extractDescopeError(result);
        const ui = mapToUiMessage(norm);
        if (ui.kind === "rate-limit" || ui.kind === "validation" || ui.kind === "policy") {
          showWarning(ui.text);
        } else {
          showError(ui.text);
        }
        setError(ui.text);
        return;
      }

      // Extract session token and user data from the result
      const jwtResponse = (result as any).data;
      console.log('[Login] JWT Response:', jwtResponse);

      if (jwtResponse) {
        // Store session token and user data in auth store
        if (jwtResponse.sessionJwt && jwtResponse.user) {
          const user = jwtResponse.user;

          // Store both token and user in Zustand store
          setAuth(
            {
              userId: user.userId,
              email: user.email,
              name: user.name,
              givenName: user.givenName,
              familyName: user.familyName,
              loginIds: user.loginIds,
              verifiedEmail: user.verifiedEmail,
              picture: user.picture,
              roleNames: user.roleNames,
            },
            jwtResponse.sessionJwt
          );

          console.log('[Login] Auth data stored in store:', {
            userId: user.userId,
            email: user.email,
            name: user.name,
          });

          // Track login event in Mixpanel
          analyticsService.trackLogin('email', user.userId);
          console.log('✅ [Login] Login event tracked in Mixpanel');
        } else {
          console.warn('[Login] Missing session token or user data in response');
        }
      }

      showSuccess("Welcome back! You have been successfully logged in.");
      setTimeout(() => navigate(from, { replace: true }), 700);
    } catch (thrown) {
      const norm = await extractDescopeError(thrown);
      const ui = mapToUiMessage(norm);
      if (ui.kind === "rate-limit" || ui.kind === "validation" || ui.kind === "policy") {
        showWarning(ui.text);
      } else {
        showError(ui.text);
      }
      setError(ui.text);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async (): Promise<void> => {
    try {
      setLoading(true);
      setError("");
      if (!sdk.oauth) throw new Error("OAuthNotConfigured");
      await sdk.oauth.start("google", window.location.href);
      showSuccess("Redirecting to Google for authentication...");
    } catch (err) {
      const ui = mapToUiMessage(await extractDescopeError(err));
      showError(ui.text);
      setError(ui.text);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = (): void => navigate("/forgot-password");
  const handleSignUp = (): void => navigate("/signup");

  return (
    <LoginLayout>
      <LoginForm
        onLogin={handleLogin}
        onGoogleLogin={handleGoogleLogin}
        onForgotPassword={handleForgotPassword}
        onSignUp={handleSignUp}
        loading={loading || isSessionLoading}
        error={error}
      />
    </LoginLayout>
  );
};

export default Login;
